import React, { useState, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Popover } from 'antd';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { ButtonWrapper } from './styles';


const formatDefaultIconName = (name) =>
  name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');


/**
 * Wrapper for Icon component to support interactions 
 * (click, hover/press, background, tooltip, keyboard support)
 */
const IconButtonV2 = ({
  type,
  size = 'md',
  color = undefined,
  selectedColor = undefined,
  selected = false,
  disabled = false,
  onClick = undefined,
  label = undefined,
  selectedBackground = true,
  hoverBackground = true,
  defaultBackground = undefined,
  tooltip = false,
  tooltipText = undefined,
  popoverContent = undefined,
  popoverTrigger = 'hover',
  popoverPlacement = 'top',
  popoverMouseEnterDelay = 0.1,
  popoverMouseLeaveDelay = 0.1,
  popoverOpen = undefined,
  onPopoverOpenChange = undefined,
  popoverOverlayClassName = undefined,
  popoverInnerStyle = undefined,
  style: styleProp = {},
  iconStyle: iconStyleProp = {},
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const isInteractive = !!onClick && !disabled;

  const iconPx = theme.iconSize[size] ?? theme.iconSize.md;

  // set background defaults
  const resolvedHoverBg = hoverBackground === true
    ? theme.backgroundLighter
    : (hoverBackground || null);
  const resolvedSelectedBg = selectedBackground === true
    ? theme.backgroundLightest
    : (selectedBackground || null);
  const hasBackground = !!(resolvedHoverBg || resolvedSelectedBg);

  const isSelectedHovered = selected && isHovered && isInteractive && !!resolvedSelectedBg;

  // set icon color defaults
  const resolvedColor = useMemo(() => {
    if (disabled) return color ?? theme.grey3;
    if (selected) return selectedColor ?? theme.primary;
    if (isHovered && isInteractive) return color ?? theme.textLight;
    return color ?? theme.textMedium;
  }, [disabled, selected, isHovered, isInteractive, color, selectedColor, theme]);

  const resolvedBackground = useMemo(() => {
    if (selected && resolvedSelectedBg) return resolvedSelectedBg;
    if (isHovered && isInteractive && resolvedHoverBg) return resolvedHoverBg;
    return defaultBackground || 'transparent';
  }, [selected, isHovered, isInteractive, resolvedSelectedBg, resolvedHoverBg, defaultBackground]);

  // press effect
  const resolvedTransform = useMemo(() => {
    if (isPressed && isInteractive) return 'scale(0.92)';
    return 'scale(1)';
  }, [isPressed, isInteractive]);

  // brightness controls
  const resolvedFilter = useMemo(() => {
    if (isPressed && isInteractive) return 'brightness(0.75)';
    if (isSelectedHovered) return 'brightness(1.25)';
    return 'none';
  }, [isPressed, isSelectedHovered, isInteractive]);

  const wrapperStyle = {
    width: iconPx,
    height: iconPx,
    background: resolvedBackground,
    borderRadius: hasBackground ? theme.br.md : undefined,
    padding: hasBackground ? theme.spacing[2] : undefined,
    transition: 'all 0.18s ease',
    transform: resolvedTransform,
    filter: resolvedFilter,
    opacity: disabled ? 0.45 : 1,
    ...styleProp,
  };

  const buttonEl = (
    <ButtonWrapper
      $disabled={disabled}
      style={wrapperStyle}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(e);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      <Icon
        type={type}
        size={size}
        color={resolvedColor}
        selected={selected}
        style={iconStyleProp}
      />
    </ButtonWrapper>
  );

  if (popoverContent) {
    // Tooltip + popover can coexist: tooltip surfaces the short label
    // on quick hover, popover provides the richer overlay after its own
    // (typically longer) delay. They render in different positions and
    // don't visually conflict.
    const controlledOpenProps = popoverOpen !== undefined
      ? { open: popoverOpen, onOpenChange: onPopoverOpenChange }
      : (onPopoverOpenChange ? { onOpenChange: onPopoverOpenChange } : {});
    const withPopover = (
      <Popover
        content={popoverContent}
        trigger={popoverTrigger}
        placement={popoverPlacement}
        mouseEnterDelay={popoverMouseEnterDelay}
        mouseLeaveDelay={popoverMouseLeaveDelay}
        overlayClassName={popoverOverlayClassName}
        overlayInnerStyle={popoverInnerStyle}
        {...controlledOpenProps}
      >
        {buttonEl}
      </Popover>
    );
    if (tooltip) {
      return (
        <Tooltip title={tooltipText ?? formatDefaultIconName(type)}>
          {withPopover}
        </Tooltip>
      );
    }
    return withPopover;
  }

  if (tooltip) {
    return (
      <Tooltip title={tooltipText ?? formatDefaultIconName(type)}>
        {buttonEl}
      </Tooltip>
    );
  }

  return buttonEl;
};

export default IconButtonV2;
